// DEPLOY: /var/www/woody.wtf/frontend/src/pages/Events.js
// Events calendar: one month at a time, every upcoming (and recent) event for the companies in view -
// earnings releases (moomoo: US dates, before / after the session, analyst estimates, options-implied move;
// ASX dates only), company events (what each company said it would deliver, from its announcements -
// expectation threads with a due date) and dividend ex-dates. Each day shows a count per kind; click a day
// for the full list, biggest companies first. Toggle the kinds on / off; filter by exchange, one of your
// lists, GICS sector and / or an ETF's holdings. Past earnings show actual vs estimate and the share price's
// reaction the first session it could move. Data: /api/events (backend/events.py).
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../api';

const KINDS = [
  { key: 'earnings', label: 'Earnings', color: 'var(--accent)' },
  { key: 'company', label: 'Company events', color: 'var(--warning)' },
  { key: 'dividends', label: 'Dividends (ex-date)', color: 'var(--success)' },
];
const SESSION = { BEFORE: 'before open', AFTER: 'after close', REGULAR: 'in session' };
const WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const iso = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const big = v => v == null ? '—' : Math.abs(v) >= 1e9 ? `${(v / 1e9).toFixed(2)}B` : Math.abs(v) >= 1e6 ? `${(v / 1e6).toFixed(0)}M` : Number(v).toFixed(0);
const num = (v, dp = 2) => v == null ? '—' : Number(v).toFixed(dp);
const surprise = (a, e) => a == null || e == null || e === 0 ? null : (a - e) / Math.abs(e) * 100;

const S = {
  page: { minHeight: '100vh', background: 'var(--bg)' },
  content: { maxWidth: '1400px', margin: '0 auto', padding: '24px 16px' },
  title: { fontSize: '20px', fontWeight: 600 },
  sub: { fontSize: '13px', color: 'var(--text-muted)', margin: '4px 0 16px' },
  bar: { display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '12px' },
  sel: { background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', color: 'var(--text)',
         fontSize: '12px', padding: '6px 8px', fontFamily: "'Space Grotesk', sans-serif" },
  btn: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', color: 'var(--text)',
         fontSize: '12px', padding: '6px 10px', cursor: 'pointer' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '4px' },
  head: { fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: 'var(--text-muted)', textAlign: 'center', padding: '4px 0' },
  cell: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', minHeight: '78px',
          padding: '6px', cursor: 'pointer', overflow: 'hidden' },
  chip: { display: 'block', fontSize: '11px', lineHeight: 1.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  panel: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 2)', padding: '14px 16px', marginTop: '16px' },
  h4: { fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)', margin: '12px 0 6px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: 'var(--text-muted)', fontWeight: 500, textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' },
  td: { padding: '5px 8px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' },
  small: { fontSize: '11px', color: 'var(--text-muted)' },
};

function Signed({ v }) {
  if (v == null) return <span style={S.small}>—</span>;
  return <span style={{ color: v >= 0 ? 'var(--success)' : 'var(--error)', fontWeight: 600 }}>{v >= 0 ? '+' : ''}{v.toFixed(1)}%</span>;
}
const Tick = ({ t }) => <Link to={`/company?ticker=${t}`} style={{ color: 'var(--accent)', textDecoration: 'none', fontFamily: "'JetBrains Mono', monospace", fontSize: '12px' }}>{t}</Link>;

function DayPanel({ day, items }) {
  const by = k => items.filter(e => e.type === k).sort((a, b) => (b.mcap || 0) - (a.mcap || 0));
  const earn = by('earnings'), comp = by('company'), div = by('dividends');
  const label = new Date(day + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return (
    <div style={S.panel}>
      <div style={{ fontWeight: 600 }}>{label}</div>
      {!items.length && <div style={S.small}>Nothing on this day for the filters and kinds selected.</div>}
      {earn.length > 0 && <>
        <div style={S.h4}>Earnings ({earn.length})</div>
        <div style={{ overflowX: 'auto' }}><table style={S.table}>
          <thead><tr>{['Company', 'When', 'EPS est / actual', 'Revenue est / actual', 'Implied move', 'Reaction'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>{earn.map((e, i) => (
            <tr key={i}>
              <td style={S.td}><Tick t={e.ticker} /> <span style={S.small}>{e.name}{e.mcap ? ` · ${big(e.mcap)}` : ''}</span></td>
              <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{SESSION[e.session] || '—'}<div style={S.small}>{e.period}</div></td>
              <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{num(e.eps_predict)} / {num(e.eps_actual)} <Signed v={surprise(e.eps_actual, e.eps_predict)} />
                {e.analysts ? <div style={S.small}>{e.analysts} analysts</div> : null}</td>
              <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{big(e.revenue_predict)} / {big(e.revenue_actual)} <Signed v={surprise(e.revenue_actual, e.revenue_predict)} /></td>
              <td style={S.td}>{e.implied_move != null ? `±${num(e.implied_move, 1)}%` : '—'}</td>
              <td style={S.td}><Signed v={e.reaction} /></td>
            </tr>))}</tbody>
        </table></div>
        {earn.some(e => e.source === 'moomoo-schedule') && <div style={S.small}>ASX rows: date only - moomoo has no ASX estimates calendar.</div>}
      </>}
      {comp.length > 0 && <>
        <div style={S.h4}>Company events - due ({comp.length})</div>
        <table style={S.table}><tbody>{comp.map((e, i) => (
          <tr key={i}>
            <td style={{ ...S.td, whiteSpace: 'nowrap' }}><Tick t={e.ticker} /></td>
            <td style={S.td}><b>{e.label}</b>{e.expected && <div style={S.small}>{e.expected}</div>}
              <div style={S.small}>{e.name}{e.due ? ` · said: ${e.due}` : ''}</div></td>
            <td style={{ ...S.td, whiteSpace: 'nowrap', color: e.status === 'delayed' ? 'var(--error)' : undefined }}>{e.outcome || e.status}</td>
          </tr>))}</tbody></table>
      </>}
      {div.length > 0 && <>
        <div style={S.h4}>Dividends going ex ({div.length})</div>
        <table style={S.table}><tbody>{div.map((e, i) => (
          <tr key={i}>
            <td style={{ ...S.td, whiteSpace: 'nowrap' }}><Tick t={e.ticker} /></td>
            <td style={S.td}>{e.name}</td>
            <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{num(e.amount, 4)} {e.currency || ''}{e.franking ? <span style={S.small}> {e.franking} franked</span> : null}</td>
            <td style={{ ...S.td, whiteSpace: 'nowrap' }}><span style={S.small}>paid</span> {e.pay_date || '—'}</td>
          </tr>))}</tbody></table>
      </>}
    </div>
  );
}

export default function Events() {
  const now = new Date();
  const [month, setMonth] = useState(new Date(now.getFullYear(), now.getMonth(), 1));
  const [kinds, setKinds] = useState(KINDS.map(k => k.key));
  const [ex, setEx] = useState('');
  const [listId, setListId] = useState('');
  const [sector, setSector] = useState('');
  const [etf, setEtf] = useState('');
  const [etfInput, setEtfInput] = useState('');
  const [opts, setOpts] = useState({ lists: [], sectors: [] });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [picked, setPicked] = useState(iso(now));

  // The grid: Monday on or before the 1st, six weeks.
  const days = useMemo(() => {
    const start = new Date(month);
    start.setDate(1 - ((month.getDay() + 6) % 7));
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [month]);

  useEffect(() => { api.get('/events/filters').then(r => setOpts(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!kinds.length) { setEvents([]); return; }
    setLoading(true); setErr('');
    const q = new URLSearchParams({ from: iso(days[0]), to: iso(days[41]), types: kinds.join(',') });
    if (ex) q.set('exchange', ex);
    if (listId) q.set('list_id', listId);
    if (sector) q.set('sector', sector);
    if (etf) q.set('etf', etf);
    api.get(`/events?${q}`).then(r => setEvents(r.data.events || []))
      .catch(e => { setErr(e.response?.data?.error || e.message); setEvents([]); }).finally(() => setLoading(false));
  }, [days, kinds, ex, listId, sector, etf]);

  const byDay = useMemo(() => {
    const m = {};
    events.forEach(e => { (m[e.date] = m[e.date] || []).push(e); });
    return m;
  }, [events]);
  const shift = n => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));
  const toggle = k => setKinds(ks => ks.includes(k) ? ks.filter(x => x !== k) : [...ks, k]);
  const today = iso(now);

  return (
    <div style={S.page}>
      <Navbar />
      <div style={S.content}>
        <div style={S.title}>Events calendar</div>
        <div style={S.sub}>Earnings, what companies said they'd deliver, and dividend ex-dates. Click a day for the details.</div>
        <div style={S.bar}>
          <button style={S.btn} onClick={() => shift(-1)}>‹</button>
          <b style={{ minWidth: 130, textAlign: 'center' }}>{month.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</b>
          <button style={S.btn} onClick={() => shift(1)}>›</button>
          <button style={S.btn} onClick={() => { setMonth(new Date(now.getFullYear(), now.getMonth(), 1)); setPicked(today); }}>Today</button>
          {KINDS.map(k => (
            <label key={k.key} style={{ fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input type="checkbox" checked={kinds.includes(k.key)} onChange={() => toggle(k.key)} />
              <span style={{ width: 8, height: 8, borderRadius: 4, background: k.color, display: 'inline-block' }} />{k.label}
            </label>))}
        </div>
        <div style={S.bar}>
          <select style={S.sel} value={ex} onChange={e => setEx(e.target.value)}>
            <option value="">All exchanges</option><option value="AU">ASX</option><option value="US">US</option>
          </select>
          <select style={S.sel} value={listId} onChange={e => setListId(e.target.value)}>
            <option value="">All companies</option>{opts.lists.map(l => <option key={l.id} value={l.id}>List: {l.name}</option>)}
          </select>
          <select style={S.sel} value={sector} onChange={e => setSector(e.target.value)}>
            <option value="">All sectors</option>{opts.sectors.map(x => <option key={x} value={x}>{x}</option>)}
          </select>
          <form onSubmit={e => { e.preventDefault(); setEtf(etfInput.trim()); }} style={{ display: 'inline-flex', gap: 4 }}>
            <input style={{ ...S.sel, width: 140 }} placeholder="ETF holdings, e.g. VAS:AU" value={etfInput} onChange={e => setEtfInput(e.target.value)} />
            {etf ? <button type="button" style={S.btn} onClick={() => { setEtf(''); setEtfInput(''); }}>✕ {etf}</button>
                 : <button type="submit" style={S.btn}>Apply</button>}
          </form>
          {loading && <span style={S.small}>Loading…</span>}
          {err && <span style={{ ...S.small, color: 'var(--error)' }}>{err}</span>}
        </div>
        <div style={S.grid}>
          {WEEK.map(w => <div key={w} style={S.head}>{w}</div>)}
          {days.map(d => {
            const k = iso(d), items = byDay[k] || [], inMonth = d.getMonth() === month.getMonth();
            return (
              <div key={k} onClick={() => setPicked(k)}
                   style={{ ...S.cell, opacity: inMonth ? 1 : 0.45,
                            outline: k === picked ? '2px solid var(--accent)' : k === today ? '1px solid var(--accent)' : 'none' }}>
                <div style={{ fontSize: '12px', fontWeight: k === today ? 700 : 500 }}>{d.getDate()}</div>
                {KINDS.filter(x => kinds.includes(x.key)).map(x => {
                  const n = items.filter(e => e.type === x.key).length;
                  return n ? <span key={x.key} style={{ ...S.chip, color: x.color }}>● {n} {x.key === 'earnings' ? 'earnings' : x.key === 'company' ? 'events' : 'ex-div'}</span> : null;
                })}
              </div>);
          })}
        </div>
        {picked && <DayPanel day={picked} items={byDay[picked] || []} />}
      </div>
    </div>
  );
}
